<script setup>
    import { ref, reactive, watch, nextTick, defineAsyncComponent, onMounted, onBeforeUnmount } from 'vue';
    import { useRouter, useRuntimeConfig } from 'nuxt/app';
    import { useRoute } from 'vue-router';

    import { useBookingStore } from '@/stores/booking';
    import { useStateStore } from '@/stores/state';
    import { useAvailabilityStore } from '@/stores/booking-availability';
    import { useErrorStore } from '@/stores/error';

    import { saveConsultation } from '~/helpers/fetchData';
    import { getBookingFailureMessage } from '~/helpers/errorMessages';
    import { Campaign } from '~/components/enums';
    import {
        scrollToElement,
        validateBookingForm,
        validateField,
    } from '~/helpers/commonUtils';
    import { getClientIpAddress } from '~/helpers/geoLocation';
    import { buildFbcFromFbclid, hasMarketingConsent } from '~/helpers/metaTracking';

    const RequestCallback = defineAsyncComponent(
        () => import('~/components/website/RequestCallback.vue')
    );
    const GiftVoucherInfo = defineAsyncComponent(
        () => import('~/components/website/GiftVoucherInfo.vue')
    );
    const ConsultationInfo = defineAsyncComponent(
        () => import('~/components/website/ConsultationInfo.vue')
    );
    import ConsultationBookingForm from '~/components/website/ConsultationBookingForm.vue';
    const SuccessMessage = defineAsyncComponent(
        () => import('~/components/website/common/SuccessMessage.vue')
    );
    import LoadingActivityIndicator from '~/components/website/common/LoadingActivityIndicator.vue';
    import AvailabilityBlock from '~/components/website/AvailabilityBlock.vue';
    import { useTracking } from '~/composables/useSocialMediaTracking';
    const DuplicateBooking = defineAsyncComponent(
        () => import('~/components/website/DuplicateBooking.vue')
    );
    import { useCookieYes } from '~/composables/useCookieYes';

    definePageMeta({
        layout: 'website',
    });

    useSeoMeta({
        title: 'Book a Free Consultation | Ultimate Performance',
        description:
            "Book your complimentary 30-minute consultation with Ultimate Performance. Speak with a trainer, discuss your goals, and discover what's possible.",
    });


    const router = useRouter();
    const route = useRoute();

    const bookingStore = useBookingStore();
    const stateStore = useStateStore();
    const availabilityStore = useAvailabilityStore();
    const errorStore = useErrorStore();
    const runtimeConfig = useRuntimeConfig();
    const mode = stateStore.getMode();

    //Clearing localStorage and sessionStorage to avoid any stale data
    bookingStore.resetState();
    stateStore.resetState();
    stateStore.clearLocalStorage();

    const getCookieValue = (name) => {
    const prefix = `${name}=`;
    const cookie = document.cookie
        .split('; ')
        .find((item) => item.startsWith(prefix));

        return cookie
            ? decodeURIComponent(cookie.slice(prefix.length))
            : '';
    };

    const readQueryParam = (value) => {
        if (Array.isArray(value)) {
            const first = value[0];
            return typeof first === 'string' ? first : '';
        }

        return typeof value === 'string' ? value : '';
    };

    const { embed, location_id, visitor_location, ...bookingQuery } = route.query;

    bookingStore.setParameters(bookingQuery);

    const preselectedLocationId = readQueryParam(location_id);
    const openedFrom = readQueryParam(embed) === '1' ? 'iframe' : 'domain';
    const visitorLocation = readQueryParam(visitor_location);

    // Centralized state
    const isSubmitted = ref(false);
    const isLoading = ref(true);
    const showGymSwitchedBanner = ref(false);

    const initialFormData = {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        marketingEmail: false,
        marketingWhatsapp: false,
        marketingSms: false,
        whatsappServiceConsent: true,
        smsServiceConsent: true,
    };

    const initialErrors = {
        firstNameError: '',
        lastNameError: '',
        emailError: '',
        phoneError: '',
        globalError: '',
    };

    const formData = reactive({ ...initialFormData });
    const errors = reactive({ ...initialErrors });

    const showRequestCallback = ref(false);
    const callbackContainer = ref(null);

    onMounted(() => {
        errorStore.clearErrors();
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    showRequestCallback.value = true;
                    observer.disconnect();
                }
            },
            {
                rootMargin: '300px'
            }
        );

        if (callbackContainer.value) {
            observer.observe(callbackContainer.value);
        }
    });

    onMounted(async () => {
        const { loadCookieYes, checkConsent } = useCookieYes();
        const { initGoogleAds, initMetaPixel } = useTracking();
        const { googleAdsId, metaPixelId } = runtimeConfig.public;

        await loadCookieYes();

        const loadTracking = () => {
            const hasMarketingConsent = checkConsent();

            if (!hasMarketingConsent) return;

            if (metaPixelId) {
                initMetaPixel(metaPixelId);
            }

            if (googleAdsId) {
                initGoogleAds(googleAdsId);
            }
        };

        const loadAfterPage = () => {
            if ('requestIdleCallback' in window) {
                window.requestIdleCallback(loadTracking, {
                    timeout: 3000,
                });
            } else {
                window.setTimeout(loadTracking, 3000);
            }
        };

        // Existing user: page has already loaded
        if (document.readyState === 'complete') {
            loadAfterPage();
        } else {
            window.addEventListener('load', loadAfterPage, {
                once: true,
            });
        }

        // New user: accepts/updates CookieYes consent
        const handleConsentUpdate = () => {
            loadAfterPage();
        };

        document.addEventListener(
            'cookieyes_consent_update',
            handleConsentUpdate
        );

        // Returning user: consent is already saved, so no consent update fires.
        // getCkyConsent isn't ready when the CookieYes script loads, so retry once the banner has loaded
        document.addEventListener(
            'cookieyes_banner_load',
            handleConsentUpdate
        );

        onBeforeUnmount(() => {
            document.removeEventListener(
                'cookieyes_consent_update',
                handleConsentUpdate
            );
            document.removeEventListener(
                'cookieyes_banner_load',
                handleConsentUpdate
            );
        });
    });

    watch(
        () => bookingStore?.state?.gym_switched,
        (newVal) => {
            if (newVal) {
                showGymSwitchedBanner.value = true;

                setTimeout(() => {
                    showGymSwitchedBanner.value = false;

                    // reset store value
                    bookingStore.updateBookingState({
                        gym_switched: false,
                    });
                }, 5000);
            }
        },
        { immediate: true }
    );

    let errorTimeout;

    watch(
        () => errors.globalError,
        (val) => {
            clearTimeout(errorTimeout);

            // Keep booking-failure help (with mailto) visible so users can contact support
            if (val && !val.includes("Oops! We couldn't complete your booking.")) {
                errorTimeout = setTimeout(() => {
                    errors.globalError = '';
                }, 15000);
            }
        }
    );

    const toggleLoadingRef = (val) => {
        isLoading.value = val;
    };

    const handleInputFieldChange = (key, value) => {
        formData[key] = value;
        if (isSubmitted.value) {
            // run validation immediately after update
            const errorKey = key + 'Error';
            if (Object.prototype.hasOwnProperty.call(errors, errorKey)) {
                validateField(key, formData[key], errors);
            }
        }
    };

    const clearFormState = async () => {
        // clear banner after sometime
         setTimeout(() => {
            bookingStore.updateBookingState({ is_duplicate: false })
        }, 20000);

        // clear state
        isSubmitted.value = false;
        bookingStore.updateBookingState({
            first_name: '',
            last_name: '',
            email: '',
            phone: '',
            marketing_consent: false,
            whatsapp_consent: false,
            sms_consent: false,
            selected_date: '',
            start_time: '',
            end_time: '',
            whatsapp_service_consent: true,
            sms_service_consent: true,
        });
        window?.iti?.setNumber('');
        Object.assign(formData, initialFormData);
        Object.assign(errors, initialErrors);
    }

    const handleSubmit = async () => {
        try {
            toggleLoadingRef(true);
            isSubmitted.value = true;
            const isGiftOfHealth = bookingStore.state.utm_campaign === Campaign.giftOfHealth2026;
            // validations
            const validationResults =
                validateBookingForm(formData, errors) &&
                Boolean(bookingStore.state.start_time);
            const voucherValidation =  !isGiftOfHealth || Boolean(bookingStore.state.voucher_code);
            if (!validationResults || !voucherValidation) {
                await nextTick();
                const ele = document.querySelector('.err-sub-text');

                if (ele) {
                    scrollToElement(ele);
                }
                errors.globalError = 'Mandatory fields not selected or stored correctly';
                throw new Error('Mandatory fields not selected or stored correctly');
            }

            const pageCookie = window.exponea?.configuration?.customer?.cookie || '' ;
            const cookieValue = bookingStore.state.cookie ? bookingStore.state.cookie : pageCookie;
            const marketingConsentGranted = hasMarketingConsent();

            const fbp = marketingConsentGranted
                ? getCookieValue('_fbp')
                : '';

            const fbc = marketingConsentGranted
                ? getCookieValue('_fbc') || buildFbcFromFbclid()
                : '';

            const clientIpAddress = await getClientIpAddress();

            // payload
            bookingStore.updateBookingState({
                first_name: formData.firstName,
                last_name: formData.lastName,
                email: formData.email,
                phone: formData.phone,
                marketing_consent: formData.marketingEmail,
                whatsapp_consent: formData.marketingWhatsapp,
                sms_consent: formData.marketingSms,
                whatsapp_service_consent: formData.whatsappServiceConsent,
                sms_service_consent: formData.smsServiceConsent,
                is_direct: true,
                opened_from: openedFrom,
                ...(visitorLocation && { visitor_location: visitorLocation }),
                cookie: cookieValue,
                fbp: fbp,
                fbc: fbc,
                client_ip_address: clientIpAddress,
                client_user_agent: navigator.userAgent,
                event_source_url: window.location.href,
            });

            //API
            await saveConsultation(
                bookingStore,
                stateStore,
                availabilityStore,
                errorStore,
                runtimeConfig,
                mode
            );
            // user redirection
            if (bookingStore.state.is_duplicate) {
                await nextTick();
                clearFormState();
                const ele = document.querySelector('#duplicate-booking-banner');
                scrollToElement(ele);
                return;
            }
            await router.push('/booking-confirmation');
        } catch (error) {
            console.error('Err:', error);

            // Keep field-validation messaging; replace API/technical failures with friendly copy
            if (
                error?.message ===
                'Mandatory fields not selected or stored correctly'
            ) {
                return;
            }

            errors.globalError = getBookingFailureMessage();
        } finally {
            toggleLoadingRef(false);
        }
    };
</script>

<template>
    <div class="bg-[#EFEDE7] text-[#161412] w-full min-h-screen">
        <div class="py-16 px-5 xl:px-20 xl:py-20">
            <LoadingActivityIndicator :show="isLoading" />

            <!-- Heading -->
            <div class="mb-12 text-center">
                <Icon
                    icon="new-up-logo"
                    class="new-brand-logo mb-5 md:mb-8"
                    aria-hidden="true"
                />

                <template v-if="bookingStore.state.utm_campaign === Campaign.giftOfHealth2026">
                    <h1 class="font-bold text-[40px] leading-[48px]">
                        Your Gift of Health starts here<span class="text-[#E4002B]">.</span>
                    </h1>

                    <p class="max-w-full md:max-w-[46rem] text-base text-[#888B8D] leading-6 mt-2 mx-auto">
                        Ready to train with the world’s leading personal trainers? Enter your voucher code, select the U.P. location shown on your voucher and choose a date and time for your consultation.
                    </p>
                </template>

                <template v-else>
                    <h1 class="font-bold text-[40px] leading-[48px]">
                        Your results start here
                    </h1>
                    <h2 class="font-bold text-3xl">
                        Book your complimentary consultation
                    </h2>
                    <p class="max-w-full md:max-w-[30rem] text-base text-[#888B8D] leading-6 mt-2 mx-auto">
                        Thirty minutes with a U.P. trainer. Discuss your goals,
                        learn how our method works, and see what's possible with the
                        right plan.
                    </p>
                </template>
            </div>

            <!-- Location changing info alert -->
            <SuccessMessage
                v-if="showGymSwitchedBanner"
                :toggle="false"
            >
                <h5 class="font-bold">
                    Currently viewing availability for
                    {{ bookingStore?.state?.display_name }}.
                </h5>
            </SuccessMessage>

            <!-- Duplicate booking banner -->
            <div v-if="bookingStore.state.is_duplicate">
                <DuplicateBooking />
            </div>

            <div class="flex flex-col gap-4 lg:grid lg:grid-cols-[7fr_3fr] lg:gap-8 lg:justify-center">

                <!-- Left column -->
                <div class="contents lg:block">
                    <!-- Location section -->
                    <div class="order-1 lg:order-none lg:mb-4">
                        <AvailabilityBlock
                            @toggle-loading="toggleLoadingRef"
                            :isSubmitted="isSubmitted"
                            :locationId="preselectedLocationId"
                        />
                    </div>

                    <!-- Booking form -->
                    <div class="order-3 lg:order-none">
                        <ConsultationBookingForm 
                            :formData="formData" 
                            :errors="errors" 
                            :isSubmitted="isSubmitted"
                            @update:field="handleInputFieldChange"
                            @toggleOption="(key) => (formData[key] = !formData[key])" @submit="handleSubmit"
                        />
                    </div>
                </div>

                <!-- Right column -->
                <div class="contents lg:block">
                    <!-- Request callback -->
                    <div ref="callbackContainer" class="order-2 lg:order-none lg:mb-4">
                        <RequestCallback v-if="showRequestCallback" />
                    </div>

                    <!-- Gift Voucher info card -->
                    <div ref="consultationInfoRef" class="order-4 lg:order-none" v-if="bookingStore.state.utm_campaign === Campaign.giftOfHealth2026">
                        <GiftVoucherInfo />
                    </div>

                    <!-- Consultation info card -->
                    <div ref="consultationInfoRef" class="order-4 lg:order-none">
                        <ConsultationInfo />
                    </div>
                </div>

            </div>
        </div>
    </div>
</template>

<style>
    .new-brand-logo {
        margin-left: auto;
        margin-right: auto;
        width: 104px !important;
        height: 42px !important;
    }
</style>
